import React from 'react';

interface EditorToolbarProps {
  dirty: boolean;
  saving: boolean;
  drawingEdge: boolean;
  diffMode: boolean;
  onAddStatus: () => void;
  onToggleDrawEdge: () => void;
  onAutoLayout: () => void;
  onSave: () => void;
  onPublish: () => void;
  onSimulate: () => void;
  onToggleDiff: () => void;
  onToggleCourtRules: () => void;
  showCourtRules: boolean;
  canPublish: boolean;
  onToggleAI?: () => void;
  showAI?: boolean;
  onOpenMigration?: () => void;
}

const btnBase = 'rounded-md px-3 py-1.5 text-xs font-medium transition-colors';
const btnDefault = `${btnBase} border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`;
const btnActive = `${btnBase} bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover`;
const btnSuccess = `${btnBase} bg-emerald-600 text-white hover:bg-emerald-700`;

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  dirty,
  saving,
  drawingEdge,
  diffMode,
  onAddStatus,
  onToggleDrawEdge,
  onAutoLayout,
  onSave,
  onPublish,
  onSimulate,
  onToggleDiff,
  onToggleCourtRules,
  showCourtRules,
  canPublish,
  onToggleAI,
  showAI,
  onOpenMigration,
}) => (
  <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 shadow-sm">
    <button type="button" onClick={onAddStatus} className={btnDefault} title="Add a new status node">
      + Status
    </button>

    <button
      type="button"
      onClick={onToggleDrawEdge}
      className={drawingEdge ? btnActive : btnDefault}
      title="Toggle draw-edge mode to connect statuses"
    >
      {drawingEdge ? 'Drawing...' : '→ Transition'}
    </button>

    <div className="h-4 w-px bg-gray-200 dark:bg-gray-700" />

    <button type="button" onClick={onAutoLayout} className={btnDefault} title="Auto-arrange nodes using directed graph layout">
      Auto-Layout
    </button>

    <button
      type="button"
      onClick={onToggleCourtRules}
      className={showCourtRules ? btnActive : btnDefault}
      title="Browse statutory deadlines"
    >
      Court Rules
    </button>

    {onToggleAI && (
      <button
        type="button"
        onClick={onToggleAI}
        className={showAI ? `${btnBase} bg-violet-600 text-white hover:bg-violet-700` : btnDefault}
        title="AI playbook generation and gap analysis"
      >
        AI Assistant
      </button>
    )}

    {onOpenMigration && (
      <button
        type="button"
        onClick={onOpenMigration}
        className={btnDefault}
        title="Import a task template as a playbook"
      >
        Import Template
      </button>
    )}

    <div className="flex-1" />

    <button
      type="button"
      onClick={onToggleDiff}
      className={diffMode ? `${btnBase} bg-violet-600 text-white hover:bg-violet-700` : btnDefault}
      title="Show diff overlay against a base version"
    >
      {diffMode ? 'Exit Diff' : 'Show Changes'}
    </button>

    <button type="button" onClick={onSimulate} className={btnDefault} title="Run simulation to test paths">
      Test
    </button>

    <button
      type="button"
      onClick={onSave}
      disabled={!dirty || saving}
      className={`${btnBase} ${dirty ? 'bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover' : 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'}`}
      title="Save layout positions"
    >
      {saving ? 'Saving...' : 'Save'}
    </button>

    {canPublish && (
      <button type="button" onClick={onPublish} className={btnSuccess} title="Validate and publish this playbook">
        Publish
      </button>
    )}
  </div>
);

export default EditorToolbar;
